import clsx from 'clsx';
import { Suspense, useMemo } from 'react';

import Skeleton from '@/components/UI/Skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/UI/table';
import { IS_SERVER } from '@/renderer/utils/ssr';
import useRequest from '@/renderer/utils/useRequest';
import type { RequestSchema } from '@/types/request';

import type { ResultsFilters } from './FilterTypes';
import { ResultsChart, ResultsChartLoading } from './ResultsChart';
import { SPORT_IDS, filterUrl, resolveSportIds } from './consts';

type Values<T> = { [K in keyof T]: T[K] }[keyof T];

type ChartResponse = API.MembershipProductStats & {
  chart: { date: string; units: string | number }[];
} & {
  playType?: Record<API.Results, API.MembershipProductStats>;
} & {
  period?: Record<API.Results, API.MembershipProductStats>;
};

const SEASONTYPES_ORDER = {
  'summer-league': 10,
  preseason: 20,
  'regular-season': 30,
  postseason: 40,
  'all-star-game': 50,
};

const LOADING_SEASONTYPES: API.ReleaseStats['seasonType'] = {
  preseason: {
    meta: null,
    risk: 0,
    type: 'seasonType',
    chunk: 'preseason',
    stats: {
      win: {
        risk: 0,
        total: 0,
        units: 0,
        period: 0,
        result: 'win',
        gameType: 'normal',
        playType: 'total',
        seasonType: 'preseason',
      },
      loss: {
        risk: 0,
        total: 0,
        units: 0,
        period: 0,
        result: 'loss',
        gameType: 'normal',
        playType: 'total',
        seasonType: 'preseason',
      },
      push: {
        risk: 0,
        total: 0,
        units: 0,
        period: 0,
        result: 'push',
        gameType: 'normal',
        playType: 'total',
        seasonType: 'preseason',
      },
      canceled: {
        risk: 0,
        total: 0,
        units: 0,
        period: 0,
        result: 'canceled',
        gameType: 'normal',
        playType: 'side',
        seasonType: 'preseason',
      },
    },
    total: 0,
    units: 0,
  },
  'regular-season': {
    meta: null,
    risk: 0,
    type: 'seasonType',
    chunk: 'regular-season',
    stats: {
      win: {
        risk: 0,
        total: 0,
        units: 0,
        period: 0,
        result: 'win',
        gameType: 'normal',
        playType: 'total',
        seasonType: 'regular-season',
      },
      loss: {
        risk: 0,
        total: 0,
        units: 0,
        period: 0,
        result: 'loss',
        gameType: 'normal',
        playType: 'total',
        seasonType: 'regular-season',
      },
      push: {
        risk: 0,
        total: 0,
        units: 0,
        period: 0,
        result: 'push',
        gameType: 'normal',
        playType: 'total',
        seasonType: 'regular-season',
      },
      pending: {
        risk: 0,
        total: 0,
        units: 0,
        period: 0,
        result: 'pending',
        gameType: 'normal',
        playType: 'total',
        seasonType: 'regular-season',
      },
      canceled: {
        risk: 0,
        total: 0,
        units: 0,
        period: 0,
        result: 'canceled',
        gameType: 'normal',
        playType: 'side',
        seasonType: 'regular-season',
      },
    },
    total: 0,
    units: 0,
  },
};

const LOADING_SEASONS: API.ReleaseStats['seasonId'] = {
  '0': {
    meta: {
      sport: {
        '0': {
          id: 0,
          name: '----',
          active: true,
          shortName: '---',
        },
      },
      season: {
        '0': {
          id: 0,
          name: '----',
          stats: {
            win: 0,
            loss: 0,
            push: 0,
            units: 0,
            pending: 0,
            canceled: 0,
            noAction: 0,
            uncharged: 0,
          },
          hidden: false,
          endDate: '2007-07-01T06:59:59+00:00',
          sportId: 0,
          createdAt: '2024-01-22T23:15:47.135288+00:00',
          startDate: '2006-07-01T07:00:00+00:00',
          updatedAt: null,
        },
      },
    },
    risk: 0,
    type: 'seasonId',
    chunk: '0',
    stats: {
      win: {
        risk: 0,
        total: 0,
        units: 0,
        result: 'win',
        seasonId: 0,
        seasonName: '----',
      },
      loss: {
        risk: 0,
        total: 0,
        units: 0,
        result: 'loss',
        seasonId: 0,
        seasonName: '----',
      },
    },
    total: 0,
    units: 0,
  },
};

const LOADING_BREAKS: (
  | Values<API.ReleaseStats['playType']>
  | Values<API.ReleaseStats['period']>
  | Values<API.ReleaseStats['gameType']>
)[] = [
  {
    meta: null,
    risk: 0,
    type: 'playType',
    chunk: 'side',
    stats: {
      win: {
        risk: 0,
        total: 0,
        units: 0,
        result: 'win',
        playType: 'side',
      },
      loss: {
        risk: 0,
        total: 0,
        units: 0,
        result: 'loss',
        playType: 'side',
      },
      push: {
        risk: 0,
        total: 0,
        units: 0,
        result: 'push',
        playType: 'side',
      },
    },
    total: 0,
    units: 0,
  },
  {
    meta: null,
    risk: 0,
    type: 'gameType',
    chunk: 'added',
    stats: {
      win: {
        risk: 0,
        total: 0,
        units: 0,
        result: 'win',
        gameType: 'added',
      },
      loss: {
        risk: 0,
        total: 0,
        units: 0,
        result: 'loss',
        gameType: 'added',
      },
      push: {
        risk: 0,
        total: 0,
        units: 0,
        result: 'push',
        gameType: 'added',
      },
    },
    total: 0,
    units: 0,
  },
];

function sum<T, L extends keyof T, K extends keyof T[L]>(obj: T, key: K) {
  let amt = 0;

  if (obj?.[key] !== undefined && typeof obj[key] !== 'object') {
    // In some cases, we return stats.win.total, and in others we have stats.win.2.total
    return +obj[key];
  }

  for (const k in obj) {
    amt += obj[k]?.[key] || 0;
  }

  return amt;
}

function toFixed(num?: number) {
  if (typeof num !== 'number') {
    return num;
  }

  return (num || 0).toFixed(2);
}

export function ResultsBreakdowns({
  baseUrl,
  userId,
  filters,
}: {
  baseUrl: string;
  userId: API.Id | 'me' | 'archive';
  filters: ResultsFilters;
}) {
  const isArchive = userId === 'archive';
  const { sportId: sportIdBase, startDate, endDate, playType, free, gameType } = filters;

  const sportId = useMemo(() => resolveSportIds(sportIdBase), [sportIdBase]);

  const serviceIds = useMemo(() => (sportIdBase === 'plus' ? [46, 59] : undefined), [sportIdBase]);

  const getChartRequest = useMemo(() => {
    const req: RequestSchema<ChartResponse> = {
      name: 'getChartRequest',
      input: `/api/v1/messageReceipt/stats/${userId}`,
    };

    return req;
  }, [userId]);
  const chartRequest = useRequest(
    getChartRequest,
    useMemo(
      () => ({
        cache: { maxAge: 90 * 1000 },
        clientOnly: !isArchive, // on /result (my results) we can't fetch this endpoint in SSR as it needs cookies
        query: {
          chart: true,
          count: true,
          tz:
            (typeof Intl !== 'undefined' &&
              Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone) ||
            undefined,
          playType:
            playType === 'full'
              ? ['side', 'total', 'prop', 'future', 'not-available']
              : playType === 'hp'
                ? ['side', 'total', 'not-available']
                : playType === 'st'
                  ? ['side', 'total']
                  : playType === 'pf'
                    ? ['prop', 'future']
                    : playType && typeof playType === 'string'
                      ? [playType]
                      : playType || ['side', 'total', 'prop', 'future', 'not-available'],
          [Array.isArray(sportId) ? 'sportId$in' : 'sportId']: sportId,
          'message.serviceIds': serviceIds,
          startDate,
          endDate,
          free,
          gameType,
          groupBy: isArchive ? 'playType' : undefined,
        },
      }),
      [isArchive, serviceIds, sportId, playType, startDate, endDate, free, gameType],
    ),
  );

  const getStatsRequest = useMemo(() => {
    if (isArchive) {
      return {
        name: 'getStatsRequest',
        input: '/api/v1/release/stats',
      } as RequestSchema<API.ReleaseStats>;
    }

    return {
      name: `getStatsRequest/${userId}`,
      input: `/api/v1/release/stats/${userId}`,
    } as RequestSchema<API.ReleaseStats>; // API.MessageReceiptReleaseStats
  }, [isArchive, userId]);

  const statsRequest = useRequest(
    getStatsRequest,
    useMemo(
      () => ({
        // careful not to cache a single person's stats to the server
        cache: !IS_SERVER || userId !== 'me' ? { maxAge: 90 * 1000 } : undefined,
        clientOnly: !isArchive,
        query: {
          data: {
            playType:
              playType === 'full'
                ? ['side', 'total', 'prop', 'future', 'not-available']
                : playType === 'hp'
                  ? ['side', 'total', 'not-available']
                  : playType === 'st'
                    ? ['side', 'total']
                    : playType === 'pf'
                      ? ['prop', 'future']
                      : playType && typeof playType === 'string'
                        ? [playType]
                        : playType || ['side', 'total', 'prop', 'future', 'not-available'],
            [Array.isArray(sportId) ? 'sportId$in' : 'sportId']: sportId,
            'message.serviceIds': serviceIds,
            'gameTime$>=': startDate,
            'gameTime$<=': endDate,
            free,
            gameType,
          },
        },
      }),
      [userId, isArchive, serviceIds, sportId, playType, startDate, endDate, free, gameType],
    ),
  );

  return (
    <ResultsBreakdownsContent
      baseUrl={baseUrl}
      userId={userId}
      filters={filters}
      chartData={chartRequest?.data}
      statsData={statsRequest?.data}
      isLoading={isArchive ? statsRequest.loading : chartRequest.loading}
      error={isArchive ? statsRequest.error : chartRequest.error}
    />
  );
}

export function ResultsBreakdownsContent({
  baseUrl,
  userId,
  filters,
  statsData,
  chartData,
  isLoading: isDataLoading,
  error,
}: {
  baseUrl: string;
  userId: API.Id | 'me' | 'archive';
  filters: ResultsFilters;
  statsData?: API.ReleaseStats;
  chartData?: ChartResponse;
  isLoading?: boolean;
  error?: Error | null | undefined;
}) {
  const isArchive = userId === 'archive';
  const { sportId: sportIdBase } = filters;

  const stats = statsData;

  const wins = (isArchive ? stats?.result.win?.total : chartData?.win) || 0;
  const losses = (isArchive ? stats?.result.loss?.total : chartData?.loss) || 0;
  const pushes = (isArchive ? stats?.result.push?.total : chartData?.push) || 0;
  const totalComplete = wins + losses + pushes;
  const unitsComplete = isArchive
    ? (stats?.result.win?.units || 0) + (stats?.result.loss?.units || 0)
    : chartData?.units || 0;
  const unitsStr = toFixed(unitsComplete);
  // const riskComplete = (stats?.result.win?.risk || 0) + (stats?.result.loss?.risk || 0);
  // const roiComplete = (isArchive ? unitsComplete / riskComplete : chartData?.roi) || 0;
  // const roiStr = toFixed(roiComplete * 100);
  const winRateWithoutPush2 = (wins / (totalComplete - pushes)) * 100 || 0;
  const winRateStr = toFixed(winRateWithoutPush2);

  const isPending = isArchive ? statsData === undefined : chartData === undefined;
  const isLoading = isDataLoading || isPending;

  const sortOrder = [
    { type: 'playType', chunk: 'side' },
    { type: 'playType', chunk: 'total' },
    { type: 'period', chunk: '1' },
    { type: 'period', chunk: '2' },
    { type: 'gameType', chunk: 'extra' },
    { type: 'gameType', chunk: 'fcs' },
    { type: 'playType', chunk: 'future' },
    { type: 'playType', chunk: 'prop' },
    { type: 'playType', chunk: 'not-available' },
  ];

  const breaks = useMemo(() => {
    const base: (
      | Values<API.ReleaseStats['playType']>
      | Values<API.ReleaseStats['period']>
      | Values<API.ReleaseStats['gameType']>
    )[] = [];

    if (stats?.playType) {
      base.push(...Object.values(stats.playType));
    }

    if (stats?.period) {
      base.push(...Object.values(stats.period));
    }

    if (stats?.gameType) {
      base.push(...Object.values(stats.gameType));
    }

    if ((!stats && isLoading) || !base.length) {
      return LOADING_BREAKS;
    }

    const sortedStats = base.sort((a, b) => {
      const orderA = sortOrder.findIndex(
        (order) => order.type === a?.type && order.chunk === a?.chunk,
      );
      const orderB = sortOrder.findIndex(
        (order) => order.type === b?.type && order.chunk === b?.chunk,
      );
      if (orderA === -1) {
        return 1;
      }
      if (orderB === -1) {
        return -1;
      }
      return orderA - orderB;
    });

    return sortedStats;
  }, [stats, isLoading]);

  const isSingleSport = useMemo(
    () =>
      sportIdBase ? Object.values(SPORT_IDS).includes(Number.parseInt(sportIdBase, 10)) : false,
    [sportIdBase],
  );

  return (
    <>
      <div className="select-none rounded-sm p-0 ring-zinc-900/10 sm:p-6 sm:pt-8 sm:pl-0 sm:shadow-xs sm:ring-1 dark:shadow-none dark:ring-white/20 sm:dark:bg-white/5">
        {/* On phones these render as three compact tiles (card background, smaller
            value, one row) so the record/win-rate/units read at a glance without the
            big desktop type; sm+ keeps the original left-aligned layout (#1542). */}
        <aside className="grid w-full grid-cols-3 gap-2 font-semibold text-2xl max-sm:text-lg sm:mb-4 sm:ml-8 sm:gap-4 sm:grid-cols-3 md:grid-cols-2 lg:grid-cols-3">
          <div className="text-left font-semibold max-sm:rounded-lg max-sm:bg-zinc-900/[0.03] max-sm:p-3 max-sm:text-center dark:max-sm:bg-white/5">
            <p className="mb-2 font-semibold text-xs text-zinc-600 uppercase tracking-wider max-sm:mb-1 dark:text-zinc-400">
              Record
            </p>
            <Skeleton loading={isLoading} className="inline-block">
              {wins || (isLoading ? '000' : 0)}-{losses || (isLoading ? '000' : 0)}-
              {pushes || (isLoading ? '00' : 0)}
            </Skeleton>
          </div>
          <div className="text-left font-semibold max-sm:rounded-lg max-sm:bg-zinc-900/[0.03] max-sm:p-3 max-sm:text-center dark:max-sm:bg-white/5">
            <p className="mb-2 font-semibold text-xs text-zinc-600 uppercase tracking-wider max-sm:mb-1 dark:text-zinc-400">
              Win rate
            </p>
            <Skeleton loading={isLoading} className="inline-block">
              {winRateStr}%
            </Skeleton>
          </div>
          <div className="text-left font-semibold max-sm:rounded-lg max-sm:bg-zinc-900/[0.03] max-sm:p-3 max-sm:text-center dark:max-sm:bg-white/5">
            <p className="mb-2 font-semibold text-xs text-zinc-600 uppercase tracking-wider max-sm:mb-1 dark:text-zinc-400">
              Units
            </p>
            <Skeleton loading={isLoading} as="span" className="inline-block">
              <span className={clsx(unitsComplete >= 0 ? 'text-emerald-500' : 'text-red-400')}>
                {unitsComplete >= 0 && '+'}
                {unitsStr}
              </span>
            </Skeleton>
          </div>
          {/* {!!roiComplete && (
          <div className="rounded-sm p-0 text-left  font-semibold ring-zinc-900/10 sm:p-4 sm:ring-1 dark:bg-white/5 dark:ring-white/20">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-600 dark:text-zinc-400">
              ROI
            </p>
            <Skeleton loading={isLoading}>{roiStr}%</Skeleton>
          </div>
        )} */}
          {error?.message || ''}
        </aside>
        <Suspense fallback={<ResultsChartLoading />}>
          <ResultsChart userId={userId} filters={filters} baseUrl={baseUrl} />
        </Suspense>
      </div>

      <section className="my-4 grid grid-cols-1 gap-8 sm:gap-4 xl:auto-cols-fr xl:grid-flow-col">
        {isSingleSport &&
          ((stats?.playType && Object.keys(stats?.playType).length > 1) || isLoading || !stats) && (
            <div className="rounded-sm p-0 text-left font-semibold ring-zinc-900/10 sm:p-4 sm:ring-1 dark:ring-white/20 sm:dark:bg-white/5">
              <p className="mb-2 font-semibold text-xs text-zinc-600 uppercase tracking-wider dark:text-zinc-400">
                Results by play type
              </p>
              <Table>
                <TableHeader className="hidden md:contents">
                  <TableRow>
                    <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 pl-0 text-left font-semibold text-gray-900 text-sm sm:pr-3 sm:pl-0 dark:bg-transparent">
                      Play type
                    </TableHead>
                    <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 text-left font-semibold text-gray-900 text-sm sm:pr-3 sm:pl-0 dark:bg-transparent">
                      Record
                    </TableHead>
                    <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 text-left font-semibold text-gray-900 text-sm sm:pr-3 sm:pl-0 dark:bg-transparent">
                      Units
                    </TableHead>
                    <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 text-left font-semibold text-gray-900 text-sm sm:pr-3 sm:pl-0 dark:bg-transparent">
                      Win rate
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {breaks.map(
                    (row) =>
                      !!row &&
                      !!TitlesByKey[row.chunk as keyof typeof TitlesByKey] && (
                        <TableRow
                          key={row.chunk}
                          className="font-normal hover:group-[&]/tbody:bg-initial"
                        >
                          <TableCell className="w-1/4 py-2 pl-0 font-semibold text-sm text-zinc-500 sm:pr-3">
                            <Skeleton
                              loading={isLoading}
                              className="inline-block whitespace-normal"
                            >
                              {TitlesByKey[row.chunk as keyof typeof TitlesByKey]}
                            </Skeleton>
                          </TableCell>
                          <TableCell className="w-1/4 whitespace-nowrap py-2 pl-0 text-sm text-zinc-500 sm:pr-3">
                            <Skeleton loading={isLoading} className="inline-block">
                              {sum(row.stats.win, 'total')}-{sum(row.stats.loss, 'total')}-
                              {sum(row.stats.push, 'total')}
                            </Skeleton>
                          </TableCell>
                          <TableCell
                            className={clsx(
                              'w-1/4 whitespace-nowrap py-2 pl-0 text-sm text-zinc-500 sm:pr-3',
                              row.units >= 0 ? 'text-emerald-500' : 'text-red-400',
                            )}
                          >
                            <Skeleton loading={isLoading} className="inline-block">
                              {row.units >= 0 ? '+' : ''}
                              {toFixed(row.units)}
                            </Skeleton>
                          </TableCell>
                          <TableCell className="w-1/4 whitespace-nowrap py-2 pl-0 text-sm text-zinc-500 sm:pr-3">
                            <Skeleton loading={isLoading} className="inline-block">
                              {toFixed(
                                (sum(row.stats.win, 'total') /
                                  (sum(row.stats.win, 'total') + sum(row.stats.loss, 'total'))) *
                                  100,
                              )}
                              %
                            </Skeleton>
                          </TableCell>
                        </TableRow>
                      ),
                  )}
                </TableBody>
              </Table>
            </div>
          )}

        {isSingleSport &&
          ((stats?.seasonType && Object.keys(stats?.seasonType).length > 1) ||
            isLoading ||
            !stats) && (
            <div className="rounded-sm p-0 text-left font-semibold ring-zinc-900/10 sm:p-4 sm:ring-1 dark:ring-white/20 sm:dark:bg-white/5">
              <p className="mb-2 font-semibold text-xs text-zinc-600 uppercase tracking-wider dark:text-zinc-400">
                Results by season type
              </p>
              <Table>
                <TableHeader className="hidden md:contents">
                  <TableRow>
                    <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 pl-4 text-left font-semibold text-gray-900 text-sm sm:pr-3 sm:pl-0 dark:bg-transparent">
                      Play type
                    </TableHead>
                    <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 pl-4 text-left font-semibold text-gray-900 text-sm sm:pr-3 sm:pl-0 dark:bg-transparent">
                      Record
                    </TableHead>
                    <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 pl-4 text-left font-semibold text-gray-900 text-sm sm:pr-3 sm:pl-0 dark:bg-transparent">
                      Units
                    </TableHead>
                    <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 pl-4 text-left font-semibold text-gray-900 text-sm sm:pr-3 sm:pl-0 dark:bg-transparent">
                      Win rate
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.values(stats?.seasonType ? stats.seasonType : LOADING_SEASONTYPES || {})
                    .sort((a, b) => {
                      const orderA = SEASONTYPES_ORDER[a.chunk] || Infinity;
                      const orderB = SEASONTYPES_ORDER[b.chunk] || Infinity;

                      return orderA - orderB;
                    })
                    .map((row) => (
                      <TableRow
                        key={row.chunk}
                        className="font-normal hover:group-[&]/tbody:bg-initial"
                      >
                        <TableCell className="w-1/4 whitespace-nowrap py-2 pl-0 font-semibold text-sm text-zinc-500 first-letter:uppercase sm:pr-3">
                          <Skeleton loading={isLoading} className="inline-block">
                            <span className="inline-block first-letter:uppercase">
                              {`${row.chunk || '-'}`.replace(/-+/g, ' ')}
                            </span>
                          </Skeleton>
                        </TableCell>
                        <TableCell className="w-1/4 whitespace-nowrap py-2 pl-0 text-sm text-zinc-500 sm:pr-3">
                          <Skeleton loading={isLoading} className="inline-block">
                            {sum(row.stats.win, 'total')}-{sum(row.stats.loss, 'total')}-
                            {sum(row.stats.push, 'total')}
                          </Skeleton>
                        </TableCell>
                        <TableCell
                          className={clsx(
                            'w-1/4 whitespace-nowrap py-2 pl-0 text-sm text-zinc-500 sm:pr-3',
                            row.units >= 0 ? 'text-emerald-500' : 'text-red-400',
                          )}
                        >
                          <Skeleton loading={isLoading} className="inline-block">
                            {row.units >= 0 ? '+' : ''}
                            {toFixed(row.units)}
                          </Skeleton>
                        </TableCell>
                        <TableCell className="w-1/4 whitespace-nowrap py-2 pl-0 text-sm text-zinc-500 sm:pr-3">
                          <Skeleton loading={isLoading} className="inline-block">
                            {toFixed(
                              (sum(row.stats.win, 'total') /
                                (sum(row.stats.win, 'total') + sum(row.stats.loss, 'total'))) *
                                100,
                            )}
                            %
                          </Skeleton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          )}

        {((stats?.seasonId && Object.keys(stats?.seasonId).length > 1) || isLoading || !stats) && (
          <div className="rounded-sm p-0 text-left font-semibold ring-zinc-900/10 sm:p-4 sm:ring-1 dark:ring-white/20 sm:dark:bg-white/5">
            <p className="mb-2 font-semibold text-xs text-zinc-600 uppercase tracking-wider dark:text-zinc-400">
              Results by season
            </p>
            <Table>
              <TableHeader className="hidden md:contents">
                <TableRow>
                  <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 pl-4 text-left font-semibold text-gray-900 text-sm sm:pr-3 sm:pl-0 dark:bg-transparent">
                    Play type
                  </TableHead>
                  <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 pl-4 text-left font-semibold text-gray-900 text-sm sm:pr-3 sm:pl-0 dark:bg-transparent">
                    Record
                  </TableHead>
                  <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 pl-4 text-left font-semibold text-gray-900 text-sm sm:pr-3 sm:pl-0 dark:bg-transparent">
                    Units
                  </TableHead>
                  <TableHead className="w-1/4 whitespace-nowrap bg-transparent py-3.5 pl-4 text-left font-semibold text-gray-900 text-sm sm:pr-3 sm:pl-0 dark:bg-transparent">
                    Win rate
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  const seasonRows = Object.values(
                    stats?.seasonId ? stats.seasonId : LOADING_SEASONS,
                  ).filter((row) => !row.meta?.season?.[row.chunk]?.hidden);

                  // When more than one sport is shown, collapse each sport's seasons into
                  // a single "<Sport> <firstYear>-<lastYear>" row with combined totals,
                  // instead of one row per season per sport.
                  const multiSport =
                    !isLoading &&
                    !!stats?.seasonId &&
                    new Set(seasonRows.map((row) => row.meta?.season?.[row.chunk]?.sportId || 0))
                      .size > 1;

                  if (multiSport) {
                    const yearOf = (n?: string) => {
                      const m = String(n || '').match(/\d{4}/);
                      return m ? Number(m[0]) : 0;
                    };
                    const ymd = (d: unknown) =>
                      d instanceof Date
                        ? `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(
                            d.getDate(),
                          ).padStart(2, '0')}`
                        : '';
                    const startStr = filters?.startDate ? ymd(filters.startDate) : '';

                    const bySport = new Map<
                      number,
                      {
                        sportId: number;
                        short: string;
                        minY: number;
                        maxY: number;
                        w: number;
                        l: number;
                        p: number;
                        u: number;
                      }
                    >();
                    for (const row of seasonRows) {
                      const season = row.meta?.season?.[row.chunk];
                      const sid = season?.sportId || 0;
                      const g = bySport.get(sid) ?? {
                        sportId: sid,
                        short: row.meta?.sport?.[sid]?.shortName || '',
                        minY: Number.POSITIVE_INFINITY,
                        maxY: 0,
                        w: 0,
                        l: 0,
                        p: 0,
                        u: 0,
                      };
                      g.w += sum(row.stats.win, 'total');
                      g.l += sum(row.stats.loss, 'total');
                      g.p += sum(row.stats.push, 'total');
                      g.u += row.units;
                      const y = yearOf(season?.name);
                      if (y) {
                        g.minY = Math.min(g.minY, y);
                        g.maxY = Math.max(g.maxY, y);
                      }
                      bySport.set(sid, g);
                    }

                    return [...bySport.values()]
                      .sort((a, b) => a.short.localeCompare(b.short))
                      .map((g) => {
                        const range =
                          g.minY && Number.isFinite(g.minY) && g.maxY
                            ? g.minY === g.maxY
                              ? `${g.minY}`
                              : `${g.minY}-${g.maxY}`
                            : '';
                        const href = `${filterUrl(baseUrl, `${g.sportId}`, undefined, '')}${
                          startStr ? `?startDate=${startStr}` : ''
                        }`;
                        return (
                          <TableRow
                            key={g.sportId}
                            className="font-normal hover:group-[&]/tbody:bg-initial"
                          >
                            <TableCell className="w-1/4 whitespace-nowrap py-2 pl-0 font-semibold text-sm text-zinc-500 sm:pr-3">
                              <a href={href}>
                                {g.short}
                                {range ? ` ${range}` : ''}
                              </a>
                            </TableCell>
                            <TableCell className="w-1/4 whitespace-nowrap py-2 pl-0 text-sm text-zinc-500 sm:pr-3">
                              {g.w}-{g.l}-{g.p}
                            </TableCell>
                            <TableCell
                              className={clsx(
                                'w-1/4 whitespace-nowrap py-2 pl-0 text-sm text-zinc-500 sm:pr-3',
                                g.u >= 0 ? 'text-emerald-500' : 'text-red-400',
                              )}
                            >
                              {g.u >= 0 ? '+' : ''}
                              {toFixed(g.u)}
                            </TableCell>
                            <TableCell className="w-1/4 whitespace-nowrap py-2 pl-0 text-sm text-zinc-500 sm:pr-3">
                              {toFixed((g.w / (g.w + g.l)) * 100)}%
                            </TableCell>
                          </TableRow>
                        );
                      });
                  }

                  return seasonRows
                    .sort(
                      (a, b) =>
                        b.meta?.season?.[b.chunk]?.name?.localeCompare?.(
                          a.meta?.season?.[a.chunk]?.name || '',
                          'en',
                        ) || 0,
                    )
                    .map((row) => (
                      <TableRow
                        key={row.chunk}
                        className="font-normal hover:group-[&]/tbody:bg-initial"
                      >
                        <TableCell className="w-1/4 whitespace-nowrap py-2 pl-0 font-semibold text-sm text-zinc-500 sm:pr-3">
                          <Skeleton loading={isLoading} className="inline-block">
                            <a
                              href={filterUrl(
                                baseUrl,
                                row.meta?.season?.[row.chunk]?.sportId,
                                row.meta?.season?.[row.chunk]?.id,
                                row.meta?.season?.[row.chunk]?.name,
                              )}
                            >
                              {row.meta?.season?.[row.chunk]?.name || '-'}{' '}
                              {row.meta?.sport?.[row.meta?.season?.[row.chunk]?.sportId || 0]
                                ?.shortName || ''}
                            </a>
                          </Skeleton>
                        </TableCell>
                        <TableCell className="w-1/4 whitespace-nowrap py-2 pl-0 text-sm text-zinc-500 sm:pr-3">
                          <Skeleton loading={isLoading} className="inline-block">
                            {sum(row.stats.win, 'total')}-{sum(row.stats.loss, 'total')}-
                            {sum(row.stats.push, 'total')}
                          </Skeleton>
                        </TableCell>
                        <TableCell
                          className={clsx(
                            'w-1/4 whitespace-nowrap py-2 pl-0 text-sm text-zinc-500 sm:pr-3',
                            row.units >= 0 ? 'text-emerald-500' : 'text-red-400',
                          )}
                        >
                          <Skeleton loading={isLoading} className="inline-block">
                            {row.units >= 0 ? '+' : ''}
                            {toFixed(row.units)}
                          </Skeleton>
                        </TableCell>
                        <TableCell className="w-1/4 whitespace-nowrap py-2 pl-0 text-sm text-zinc-500 sm:pr-3">
                          <Skeleton loading={isLoading} className="inline-block">
                            {toFixed(
                              (sum(row.stats.win, 'total') /
                                (sum(row.stats.win, 'total') + sum(row.stats.loss, 'total'))) *
                                100,
                            )}
                            %
                          </Skeleton>
                        </TableCell>
                      </TableRow>
                    ));
                })()}
              </TableBody>
            </Table>
          </div>
        )}
      </section>
    </>
  );
}

const TitlesByKey = {
  total: 'Totals',
  side: 'Sides',
  '1': '1st halfs',
  '2': '2nd halfs',
  extra: 'Extras',
  fcs: 'FCS',
  addon: 'Add-ons',
  future: 'Futures',
  prop: 'Props',
  'not-available': 'Unidentified play type (pre 2005)',
};
